import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import Box from "../../../components/Box";
import { Helpers } from "../../../helpers/Helpers";
import { KTCard } from "../../../_metronic/helpers/components/KTCard";
import { KTCardBody } from "../../../_metronic/helpers/components/KTCardBody";
import Content from "../../../_metronic/layout/components/content/Content";
import { ToolbarWrapper } from "../../../_metronic/layout/components/toolbar";
import { PageTitle } from "../../../_metronic/layout/core";
import { storeActivityIndicator, storeAreYouSureModalCallback } from "../../../redux/reducers/authSlice";
import CustomSingleSelect, { CustomSingleSelectModel } from "../../../components/CustomSingleSelect";
import CustomNumberInput from "../../../components/CustomNumberInput";
import CustomTextInput from "../../../components/CustomTextInput";
import CustomDatePicker from "../../../components/CustomDatePicker";
import CustomSwitch from "../../../components/CustomSwitch";
import { PersonnelService } from "../../../services/PersonnelService";
import { SABIT_GELIR_TURLERI, SabitGelirPoSatiri, SabitGelirService, sabitGelirPaylari, sabitGelirTurAdi, tl } from "../../../services/SabitGelirService";

/** Dağıtım grid satırı: bir PO Eşleştirme satırı (Müşteri + Bölüm + PO + PO No) ve oranı. */
interface DagitimSatiri {
    anahtar: string
    companyPoId: string
    customerName?: string | null
    divisionName?: string | null
    poName?: string | null
    poNo?: string | null
    oran: string
    isSelected: boolean
}

let anahtarSayaci = 1;
const yeniAnahtar = () => 'satir-' + (anahtarSayaci++);

const turSecenegi = (tur: number): CustomSingleSelectModel => ({ label: sabitGelirTurAdi(tur), value: String(tur) });

/**
 * Sabit Gelir Dağıtımı kartı (standart kayıt ekranı, 09.10.2026; PO Eşleştirme kaydı + sipariş detay grid kalıbı).
 * Bedel seçilen "Aylık Sabit Bedel" şablonunun dönemi kapsayan anlaşmasından gelir; dağıtım satırları
 * PO Eşleştirme'deki "Depo Kira ve Personel" projesi satırlarıdır.
 */
const SabitGelirAdd = () => {
    const navigate = useNavigate();
    const pageName = "Kargo Modülü > Nakliye > Sabit Gelir Dağıtımı"
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();
    const dagitimRef = useRef<any>(null);

    const [sabitGelirKartiId, setSabitGelirKartiId] = useState<string | undefined>(undefined);
    const [selectedTur, setSelectedTur] = useState<CustomSingleSelectModel | undefined>(turSecenegi(1));
    const [selectedSablon, setSelectedSablon] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [selectedPersonel, setSelectedPersonel] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [ad, setAd] = useState<string | undefined>(undefined);
    const [startDate, setStartDate] = useState<string | undefined>(undefined);
    const [endDate, setEndDate] = useState<string | undefined>(undefined);
    const [isCustomActive, setIsCustomActive] = useState<boolean>(true);
    const [dagitimlar, setDagitimlar] = useState<DagitimSatiri[]>([]);

    // Dağıtım grid'indeki ekleme / güncelleme formu
    const [isOpen, setIsOpen] = useState(false);
    const [duzenlenenAnahtar, setDuzenlenenAnahtar] = useState<string | undefined>(undefined);
    const [selectedPoSatiri, setSelectedPoSatiri] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [oran, setOran] = useState<string | undefined>(undefined);
    const [poSatirlari, setPoSatirlari] = useState<SabitGelirPoSatiri[]>([]);

    const tur = Number(selectedTur?.value ?? 1);

    useEffect(() => {
        var id = searchParams.get('id');

        if (id)
            getById(id);
    }, [])

    const getById = async (_id: string) => {
        try {
            dispatch(storeActivityIndicator(true));

            var response = await SabitGelirService.getById(_id);

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            var k = response.data;

            setSabitGelirKartiId(k.sabitGelirKartiId);
            setSelectedTur(turSecenegi(k.tur));
            setSelectedSablon({ label: k.agreementName ?? '', value: k.ambarAgreementSchemeId, additionalValue1: k.bedel != null ? String(k.bedel) : undefined });
            setSelectedPersonel(k.personnelId ? { label: k.personnelName ?? '', value: k.personnelId } : undefined);
            setAd(k.ad ?? undefined);
            setStartDate(k.startDate ?? undefined);
            setEndDate(k.endDate ?? undefined);
            setIsCustomActive(k.isCustomActive);
            setDagitimlar(k.dagitimlar.map(d => ({
                anahtar: yeniAnahtar(),
                companyPoId: d.companyPoId,
                customerName: d.customerName,
                divisionName: d.divisionName,
                poName: d.poName,
                poNo: d.poNo,
                oran: String(d.oran).replace('.', ','),
                isSelected: false
            })));
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const bedel = selectedSablon?.additionalValue1 ? Number(selectedSablon.additionalValue1) : undefined;
    const oranlar = dagitimlar.map(x => Helpers.toNumber(x.oran));
    const toplamOran = Math.round(oranlar.reduce((a, b) => a + b, 0) * 10000) / 10000;
    const oranTam = Math.abs(toplamOran - 100) < 0.0001;
    const paylar = bedel != null ? sabitGelirPaylari(bedel, oranlar) : [];

    // ---------------------------------------------------------------- Dağıtım grid'i

    const scrollTo = () => {
        if (!dagitimRef.current)
            return;

        var headerOffset = 120;
        var elementPosition = dagitimRef.current.getBoundingClientRect().top;
        var offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
            top: offsetPosition,
            behavior: "smooth"
        });
    }

    const formuTemizle = () => {
        setDuzenlenenAnahtar(undefined);
        setSelectedPoSatiri(undefined);
        setOran(undefined);
    }

    const cancelAdding = () => {
        if (!isOpen) {
            formuTemizle();
            setIsOpen(true);
            scrollTo();
            return;
        }

        formuTemizle();
        setIsOpen(false);
        scrollTo();
    }

    const poSatirlariniGetir = async () => {
        var response = await SabitGelirService.poSatirlari();

        if (response.isSuccess)
            setPoSatirlari(response.data);

        // #510: kira tevkifatsız kesilir; 'tevkifatlı' bilgisi yalnız Personel kartında gösterilir.
        return SabitGelirService.secenek(Promise.resolve(response), x => ({ label: x.etiket + (x.tevkifatli && tur == 2 ? ' · tevkifatlı' : ''), value: x.companyPoId }));
    }

    const saveItem = () => {
        if (!selectedPoSatiri) {
            toast.error("PO Eşleştirme Satırı alanı zorunludur.");
            return;
        }

        var yeniOran = Helpers.toNumber(oran);

        if (!(yeniOran > 0) || yeniOran > 100) {
            toast.error("Oran 0'dan büyük ve en çok 100 olmalıdır.");
            return;
        }

        if (dagitimlar.some(x => x.companyPoId == selectedPoSatiri.value && x.anahtar != duzenlenenAnahtar)) {
            toast.error("Bu PO Eşleştirme satırı dağıtımda zaten var.");
            return;
        }

        var po = poSatirlari.find(x => x.companyPoId == selectedPoSatiri.value);
        var eski = dagitimlar.find(x => x.anahtar == duzenlenenAnahtar);

        var satir: DagitimSatiri = {
            anahtar: duzenlenenAnahtar ?? yeniAnahtar(),
            companyPoId: selectedPoSatiri.value,
            customerName: po?.customerName ?? (eski?.companyPoId == selectedPoSatiri.value ? eski?.customerName : selectedPoSatiri.label),
            divisionName: po?.divisionName ?? (eski?.companyPoId == selectedPoSatiri.value ? eski?.divisionName : undefined),
            poName: po?.poName ?? (eski?.companyPoId == selectedPoSatiri.value ? eski?.poName : undefined),
            poNo: po?.poNo ?? (eski?.companyPoId == selectedPoSatiri.value ? eski?.poNo : undefined),
            oran: oran ?? '',
            isSelected: false
        };

        setDagitimlar(duzenlenenAnahtar
            ? dagitimlar.map(x => x.anahtar == duzenlenenAnahtar ? satir : x)
            : [...dagitimlar, satir]);

        formuTemizle();
        setIsOpen(false);
        scrollTo();
    }

    const setItemSelected = (_anahtar: string) => {
        setDagitimlar(dagitimlar.map(x => x.anahtar == _anahtar ? { ...x, isSelected: !x.isSelected } : x));
    }

    const selectAll = () => {
        var isAllSelected = dagitimlar.length == dagitimlar.filter(x => x.isSelected).length;

        setDagitimlar(dagitimlar.map(x => ({ ...x, isSelected: !isAllSelected })));
    }

    const setUpdateItem = () => {
        var o = dagitimlar.filter(x => x.isSelected)[0];

        setDuzenlenenAnahtar(o.anahtar);
        setSelectedPoSatiri({ label: [o.customerName, o.divisionName, o.poName, o.poNo].filter(x => x).join(' · '), value: o.companyPoId });
        setOran(o.oran);

        setIsOpen(true);
        scrollTo();
    }

    const deleteItems = async () => {
        setDagitimlar(dagitimlar.filter(x => !x.isSelected));
    }

    // ---------------------------------------------------------------- Kaydet

    const saveSabitGelir = async () => {
        try {
            if (!selectedSablon) {
                toast.error("Anlaşma Şablonu alanı zorunludur.");
                return;
            }

            if (tur == 2 && !selectedPersonel) {
                toast.error("Personel alanı zorunludur.");
                return;
            }

            if (dagitimlar.length == 0) {
                toast.error("Dağıtıma en az bir PO Eşleştirme satırı ekleyiniz.");
                return;
            }

            if (!oranTam) {
                toast.error(`Oranların toplamı %100 olmalıdır (şu an %${toplamOran.toLocaleString('tr-TR')}).`);
                return;
            }

            dispatch(storeActivityIndicator(true));

            var response = await SabitGelirService.kaydet({
                sabitGelirKartiId: sabitGelirKartiId,
                tur: tur,
                ad: ad || undefined,
                ambarAgreementSchemeId: selectedSablon.value,
                personnelId: tur == 2 ? selectedPersonel?.value : undefined,
                startDate: startDate || undefined,
                endDate: endDate || undefined,
                isCustomActive: isCustomActive,
                dagitimlar: dagitimlar.map(x => ({ companyPoId: x.companyPoId, oran: Helpers.toNumber(x.oran) }))
            });

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                dispatch(storeActivityIndicator(false));
                return;
            }

            toast.success("Kayıt başarılı.");

            dispatch(storeActivityIndicator(false));
            navigate(Helpers.getGoBackUrl())
        } catch (error: any) {
            toast.error(error);
            dispatch(storeActivityIndicator(false));
        }
    }

    const seciliSayisi = dagitimlar.filter(x => x.isSelected).length;

    return (
        <React.Fragment>
            <ToolbarWrapper filters={undefined} />

            <PageTitle breadcrumbs={[{
                title: pageName.split('>').slice(0, pageName.split('>').length - 1).join(' > ') + ' > ',
                isActive: false,
                path: ''
            }]}>{(pageName.split('>')[pageName.split('>').length - 1])}</PageTitle>

            <Box className="mb-10">
                <Content>
                    <div>
                        <KTCard className="ebbe-card">
                            <KTCardBody>
                                <h4 className="mb-10 text-black">Genel Bilgiler</h4>

                                <div className="row">
                                    <div className="col-md-4">
                                        <CustomSingleSelect
                                            labelText="Gelir Türü"
                                            isLabelRequired
                                            selectedValue={selectedTur}
                                            onChange={(value) => {
                                                setSelectedTur(value ?? turSecenegi(1));
                                                if (value?.value != '2')
                                                    setSelectedPersonel(undefined);
                                            }}
                                            onMenuOpen={() => Promise.resolve({ isSuccess: true, data: SABIT_GELIR_TURLERI.map(x => ({ label: x.label, value: String(x.value) })) })}
                                            labelName='label'
                                            valueName='value'
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <CustomSingleSelect
                                            labelText="Anlaşma Şablonu (Aylık Sabit Bedel)"
                                            isLabelRequired
                                            selectedValue={selectedSablon}
                                            onChange={setSelectedSablon}
                                            onMenuOpen={() => SabitGelirService.secenek(SabitGelirService.sablonlar(), x => ({
                                                label: `${x.name}${x.guncelBedel != null ? ' · ' + tl(x.guncelBedel) + ' ' + (x.currencyTypeName ?? '') : ' · bugün geçerli anlaşma yok'} · ${x.dinamik ? 'Hizmet: dinamik (PO)' : 'Hizmet: ' + (x.hizmetAdi ?? 'seçilmemiş')}`,
                                                value: x.ambarAgreementSchemeId,
                                                additionalValue1: x.guncelBedel != null ? String(x.guncelBedel) : undefined
                                            }))}
                                            labelName='label'
                                            valueName='value'
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <CustomTextInput labelText="Aylık Bedel" value={bedel != null ? tl(bedel) : ''} setValue={undefined} isDisabled />
                                    </div>
                                </div>

                                <div className="row mt-5">
                                    {
                                        tur == 2 && (
                                            <div className="col-md-4">
                                                <CustomSingleSelect
                                                    labelText="Personel"
                                                    isLabelRequired
                                                    selectedValue={selectedPersonel}
                                                    onChange={setSelectedPersonel}
                                                    onMenuOpen={() => PersonnelService.getPersonnelList(undefined, true)}
                                                    labelName='nameSurname'
                                                    valueName='personnelId'
                                                />
                                            </div>
                                        )
                                    }

                                    <div className={tur == 2 ? "col-md-8" : "col-md-4"}>
                                        <CustomTextInput labelText={tur == 2 ? "Not" : "Kart Adı"} value={ad} setValue={setAd} />
                                    </div>
                                </div>

                                <div className="row mt-5">
                                    <div className="col-md-4">
                                        <CustomDatePicker labelText="Başlangıç Tarihi" value={startDate} setValue={setStartDate} />
                                    </div>

                                    <div className="col-md-4">
                                        <CustomDatePicker labelText="Bitiş Tarihi" value={endDate} setValue={setEndDate} />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label">Aktif</label>

                                        <CustomSwitch isActive={isCustomActive} setIsActive={setIsCustomActive} />
                                    </div>
                                </div>

                                <div className="text-muted fs-7 mt-5">
                                    {tur == 1 ? 'Kira tevkifatsız faturalanır; birim fiyat aylık kira tutarıdır.' : 'Personel 606 (9/10) tevkifatlı faturalanır, kira ile aynı faturaya girmez; birim fiyat kişi başı aylık bedeldir.'}
                                    {' '}Şablonlar: Kargo Modülü › Nakliye › Anlaşma Şablonu (çarpanı "Aylık Sabit Bedel" olan gelir şablonları). Faturadaki hizmet kalemi şablondan gelir; şablonda "Dinamik Hizmet Kalemi" açıksa dağıtım satırının PO'su kullanılır.
                                </div>
                            </KTCardBody>
                        </KTCard>
                    </div>

                    <div className='containerPart' ref={dagitimRef}>
                        <KTCard className="ebbe-card mt-10">
                            <KTCardBody>
                                <h4 className="mb-10 text-black">Dağıtım</h4>

                                {
                                    isOpen && (
                                        <React.Fragment>
                                            <div className="row">
                                                <div className="col-md-8">
                                                    <CustomSingleSelect
                                                        labelText="PO Eşleştirme Satırı (Müşteri · Bölüm · PO · PO Numarası)"
                                                        isLabelRequired
                                                        selectedValue={selectedPoSatiri}
                                                        onChange={setSelectedPoSatiri}
                                                        onMenuOpen={poSatirlariniGetir}
                                                        labelName='label'
                                                        valueName='value'
                                                    />
                                                </div>

                                                <div className="col-md-4">
                                                    <CustomNumberInput labelText="Oran %" isLabelRequired value={oran} setValue={setOran} />
                                                </div>
                                            </div>

                                            <div className="row mt-5">
                                                <div className="col-md-12">
                                                    <Box alignItems="flex-end">
                                                        <Box onClick={saveItem} className="actionIcon p-2 bg-success" justifyContent="center" flexDirection="row" borderRadius="4px">
                                                            <Box mr="5px" justifyContent="center">
                                                                <i className="text-white fa fa-check" />
                                                            </Box>
                                                            <Box justifyContent="center">
                                                                <span style={{ fontWeight: 600 }} className="text-white">Kaydet</span>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </div>
                                            </div>

                                            <Box className='ebbe-hr m-0 mt-5'>&nbsp;</Box>
                                        </React.Fragment>
                                    )
                                }

                                <Box className={isOpen ? "mt-5" : ""} flexDirection="row">
                                    <Box onClick={cancelAdding} className={"actionIcon p-2 " + (!isOpen ? "bg-success" : "")} opacity={"1"} bg={isOpen ? 'orange' : undefined} justifyContent="center" flexDirection="row" borderRadius="4px">
                                        <Box mr="5px" justifyContent="center">
                                            <i className={"text-white fa " + (!isOpen ? 'fa-plus' : 'fa-times')} />
                                        </Box>
                                        <Box justifyContent="center">
                                            <span style={{ fontWeight: 600 }} className="text-white">{!isOpen ? 'Ekle' : 'İptal Et'}</span>
                                        </Box>
                                    </Box>

                                    <Box onClick={seciliSayisi == 1 ? setUpdateItem : undefined} opacity={seciliSayisi == 1 ? "1" : "0.5"} ml="10px" className="actionIcon p-2 bg-primary" justifyContent="center" flexDirection="row" borderRadius="4px">
                                        <Box mr="5px" justifyContent="center">
                                            <i className="text-white fa fa-edit" />
                                        </Box>
                                        <Box justifyContent="center">
                                            <span style={{ fontWeight: 600 }} className="text-white">Güncelle</span>
                                        </Box>
                                    </Box>

                                    <Box onClick={seciliSayisi > 0 ? () => dispatch(storeAreYouSureModalCallback(deleteItems)) : undefined} opacity={seciliSayisi > 0 ? "1" : "0.5"} ml="10px" className="actionIcon p-2 bg-danger" justifyContent="center" flexDirection="row" borderRadius="4px">
                                        <Box mr="5px" justifyContent="center">
                                            <i className="text-white fa fa-trash" />
                                        </Box>
                                        <Box justifyContent="center">
                                            <span style={{ fontWeight: 600 }} className="text-white">Sil</span>
                                        </Box>
                                    </Box>
                                </Box>

                                <div className="row mt-5">
                                    <div className="col-md-12">
                                        <div className='table-responsive'>
                                            <table id='kt_table_users' className='table align-middle table-row-dashed dataTable no-footer'>
                                                <thead>
                                                    <tr className='text-start text-muted fw-bolder text-uppercase'>
                                                        <th style={{ width: 1 }}>
                                                            <div className='d-flex'>
                                                                <label className='form-check form-check-sm form-check-custom form-check-solid'>
                                                                    <input onChange={selectAll} className='form-check-input' type='checkbox' checked={seciliSayisi > 0 && seciliSayisi == dagitimlar.length} />
                                                                </label>
                                                            </div>
                                                        </th>
                                                        <th>Müşteri</th>
                                                        <th>Bölüm</th>
                                                        <th>PO</th>
                                                        <th>PO Numarası</th>
                                                        <th>Oran %</th>
                                                        <th>Aylık Tutar</th>
                                                    </tr>
                                                </thead>
                                                <tbody className='text-gray-600 fw-bold'>
                                                    {
                                                        dagitimlar.length == 0 && (
                                                            <tr>
                                                                <td className="text-center" colSpan={99}>Kayıt bulunamadı.</td>
                                                            </tr>
                                                        )
                                                    }
                                                    {
                                                        dagitimlar.map((item, index) =>
                                                            <tr key={item.anahtar}>
                                                                <td style={{ width: 1 }}>
                                                                    <div className='d-flex'>
                                                                        <label className='form-check form-check-sm form-check-custom form-check-solid'>
                                                                            <input onChange={() => setItemSelected(item.anahtar)} className='form-check-input' type='checkbox' checked={item.isSelected} />
                                                                        </label>
                                                                    </div>
                                                                </td>
                                                                <td>{item.customerName}</td>
                                                                <td>{item.divisionName}</td>
                                                                <td>{item.poName}</td>
                                                                <td>{item.poNo}</td>
                                                                <td>{Helpers.correctPrice(oranlar[index], 2)}</td>
                                                                <td>{bedel != null ? tl(paylar[index]) : ''}</td>
                                                            </tr>
                                                        )
                                                    }
                                                </tbody>
                                                {
                                                    dagitimlar.length > 0 && (
                                                        <tfoot>
                                                            <tr className="ebbe-dip-toplam">
                                                                <td style={{ width: 1 }}></td>
                                                                <td>Toplam</td>
                                                                <td colSpan={3}>{!oranTam && <span className="text-danger">Oranların toplamı %100 olmalıdır.</span>}</td>
                                                                <td className={oranTam ? undefined : 'text-danger'}>{Helpers.correctPrice(toplamOran, 2)}</td>
                                                                <td>{bedel != null ? tl(paylar.reduce((a, b) => a + b, 0)) : ''}</td>
                                                            </tr>
                                                        </tfoot>
                                                    )
                                                }
                                            </table>
                                        </div>

                                        <div className="text-muted fs-7">PO satırları: Genel Tanımlamalar › PO Eşleştirme, proje "Depo Kira ve Personel". Yuvarlamadan kalan kuruş son satıra eklenir; toplam her zaman aylık bedele eşittir.</div>
                                    </div>
                                </div>
                            </KTCardBody>
                        </KTCard>
                    </div>

                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <Box flexDirection="row" justifyContent="flex-end">
                                <Box mr="15px">
                                    <button className="btn btn-sm btn-light btn-active-light-primary" onClick={() => navigate(Helpers.getGoBackUrl())}>Geri</button>
                                </Box>
                                <Box>
                                    <button className="btn btn-primary btn-sm" onClick={saveSabitGelir}>Kaydet</button>
                                </Box>
                            </Box>
                        </KTCardBody>
                    </KTCard>
                </Content>
            </Box>
        </React.Fragment>
    )
}

export default SabitGelirAdd;
